// AppContent.js - Contenu de l'application sans le Router
// Le Router sera ajouté par le serveur SSR (StaticRouter) ou le client (BrowserRouter)
import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import Home from "pages/Home";
import Header from "components/layout/Header";
import Footer from "components/layout/Footer";
import Layout from "components/layout/Layout";
import Page from "pages/Page";
import CategoriePage from "pages/CategoriePage";
import SearchResults from "pages/SearchResults";
import EventPage from "pages/EventPage";
import Agenda from "pages/AgendaPage";
import Blog from "pages/BlogPage";
import NewsPage from "pages/NewsPage";
import MapPage from "pages/MapPage";
import PointOfInterestPage from "pages/PointOfInterestPage";
import KioskPage from "pages/KioskPage";
import KioskSeriePage from "pages/KioskSeriePage";
import MagazinePage from "pages/MagazinePage";
import AssociationsPage from "pages/AssociationsPage";
import AssociationPage from "pages/AssociationPage";
import MerchantsPage from "pages/MerchantsPage";
import MerchantPage from "pages/MerchantPage";
import ContactPage from "pages/ContactPage";
import CookiesPage from "pages/CookiesPage";
import EcrireMairePage from "pages/EcrireMairePage";
import MentionsLegalesPage from "pages/MentionsLegalesPage";
import UrgenceNumbersPage from "pages/UrgenceNumbersPage";
import ActesAdministratifsPage from "pages/ActesAdministratifsPage";
import WebcamPage from "pages/WebcamPage";
import SearchResultsSubsite from "pages/SearchResultsSubsite";
import NotFoundPage from "pages/errors/NotFoundPage";
import DynamicSubSiteRoute from "components/routes/DynamicSubSiteRoute";
import ProfileSidebar from "components/settings/ProfileSidebar";
import AccessibilitySidebar from "components/settings/AccessibilitySidebar";
import Modal from "components/common/Modal";
import Sidebar from "components/navigation/Sidebar";
import ScrollToTop from "./components/routes/ScrollToTop";
import { AccessibilityProvider } from "contexts/AccessibilityContext";
import { ProfileProvider } from "contexts/ProfileContext";
import { ProfileSidebarProvider } from "./contexts/ProfileSidebarContext";
import { AccessibilitySidebarProvider } from "contexts/AccessibilitySidebarContext";
import { ModalProvider } from "contexts/ModalContext";
import { SubSiteProvider } from "contexts/SubSiteContext";
import "./App.css";
import "styles/variables.css";
import "styles/main.css";
import "styles/access.css";
import "styles/fc.css";
import { Helmet } from 'react-helmet-async';
import api from "services/api";
import Spinner from "components/common/Spinner";
import ComingSoonPage from "pages/ComingSoonPage";
import SubSiteComingSoonPage from "pages/subsite/SubSiteComingSoonPage";
import { usePreloadedData } from "contexts/PreloadedDataContext";
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import redirectionRoutes  from "components/routes/Redirections";
import MatomoScript from './MatomoScript';

// Composant pour forcer une redirection avec rechargement complet
// Cela garantit que le contexte SubSite est correctement initialisé
const HardRedirect = ({ to }) => {
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.location.href = to;
    }
  }, [to]);
  
  return <Spinner />;
};

// Remplace un sous-site temporaire fermé par la page d'attente, avec la charte du site principal.
// Composant séparé : seul lui dépend de l'URL, AppContent ne se re-rend pas à chaque navigation.
const ClosedSubSiteGate = ({ closedSubSites, children }) => {
  const location = useLocation();
  const firstPathSegment = location.pathname.split('/').filter(Boolean)[0] || null;
  const closedSubSite = firstPathSegment
    ? closedSubSites.find(subSite => subSite.slug === firstPathSegment)
    : null;

  if (!closedSubSite) {
    return children;
  }

  return (
    <SubSiteProvider key="closed-subsite" forceMainSite>
      <Layout>
        <Header />
        <ScrollToTop />
        <SubSiteComingSoonPage name={closedSubSite.name} />
        <Footer forceMainSite />
      </Layout>
      <ProfileSidebar />
      <AccessibilitySidebar />
      <Sidebar />
    </SubSiteProvider>
  );
};

function AppContent() {
  const isRedirectionEnabled = process.env.REACT_APP_ENABLE_REDIRECTION === "true";

  // Gestion du scroll restoration
  if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
    window.history.scrollRestoration = 'manual';
  }

  // Initialiser loading à false pour éviter le mismatch d'hydratation
  const [isLanding, setIsLanding] = useState(false);
  const [loading, setLoading] = useState(false);

  // Sous-sites temporaires fermés : préchargés par le SSR quand l'URL en cible un, puis rafraîchis côté client
  const preloadedClosedSubSites = usePreloadedData('closedSubSites');
  const [closedSubSites, setClosedSubSites] = useState(preloadedClosedSubSites || []);
  const [closedSubSitesLoading, setClosedSubSitesLoading] = useState(false);

  useEffect(() => {
    // En SSR, on ne charge pas les données de landing
    if (typeof window === 'undefined') {
      setLoading(false);
      return;
    }
    setLoading(true);
    api.get('/landing')
      .then(response => {
        if (response.data && response.data.isLanding === true) {
          setIsLanding(true);
        }
        setLoading(false);
      })
      .catch(error => {
        console.error('Erreur lors de la récupération des données de landing:', error);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    // Indicateur utile seulement sans préchargement SSR : évite un re-rendu complet sinon
    if (!preloadedClosedSubSites) {
      setClosedSubSitesLoading(true);
    }
    api.get('/subsites/closed')
      .then(response => {
        const data = Array.isArray(response.data) ? response.data : [];
        // Pas de mise à jour si la liste préchargée est identique (évite un re-rendu complet)
        setClosedSubSites(current => (JSON.stringify(current) === JSON.stringify(data) ? current : data));
      })
      .catch(error => {
        console.error('Erreur lors de la récupération des sous-sites fermés:', error);
      })
      .finally(() => {
        setClosedSubSitesLoading(false);
      });
  }, []);

  // Ne pas rendre Spinner en SSR pour éviter le mismatch
  if ((loading || (closedSubSitesLoading && !preloadedClosedSubSites)) && typeof window !== 'undefined') {
    return <Spinner />;
  }

  // Vérifier si reCAPTCHA doit être initialisé (seulement côté client avec clé)
  const recaptchaKeyRaw = typeof window !== 'undefined' 
    ? process.env.REACT_APP_RECAPTCHA_SITE_KEY 
    : undefined;
  
  const recaptchaKey = recaptchaKeyRaw && typeof recaptchaKeyRaw === 'string'
    ? recaptchaKeyRaw.trim()
    : '';
  
  const isValidKey = recaptchaKey 
    && recaptchaKey.length > 10 
    && recaptchaKey.startsWith('6L')
    && recaptchaKey !== 'undefined'
    && recaptchaKey !== 'null';
  
  const shouldRenderRecaptcha = typeof window !== 'undefined' && isValidKey;

  const RecaptchaWrapper = ({ children }) => {
    const [recaptchaError, setRecaptchaError] = React.useState(false);

    if (!shouldRenderRecaptcha) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[reCAPTCHA] Désactivé - clé non fournie ou invalide');
      }
      return <>{children}</>;
    }

    if (recaptchaError) {
      console.warn('reCAPTCHA non disponible, fonctionnement sans protection');
      return <>{children}</>;
    }

    if (!recaptchaKey || !recaptchaKey.startsWith('6L') || recaptchaKey.length < 10) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[reCAPTCHA] Clé invalide ou manquante, désactivation');
        console.log('[reCAPTCHA] Valeur détectée:', recaptchaKey ? `${recaptchaKey.substring(0, 10)}...` : 'vide');
      }
      return <>{children}</>;
    }

    return (
      <GoogleReCaptchaProvider 
        reCaptchaKey={recaptchaKey}
        useRecaptchaNet={false}
        useEnterprise={false}
        reCaptchaType="v3"
      >
        {children}
      </GoogleReCaptchaProvider>
    );
  };

  return (
    <div>
      {typeof window !== 'undefined' && process.env.NODE_ENV === 'production' && <MatomoScript />}
      <AccessibilityProvider>
        <AccessibilitySidebarProvider>
          <ProfileSidebarProvider>
            <ProfileProvider>
              <ModalProvider>
                <RecaptchaWrapper>
                  {isLanding ? (
                    <Routes>
                      <Route path="/" element={<ComingSoonPage />} />
                      <Route path="*" element={<Navigate to="/" />} />
                    </Routes>
                  ) : (
                    <ClosedSubSiteGate closedSubSites={closedSubSites}>
                      <SubSiteProvider key="site">
                        <Layout>
                          <Header />
                          <ScrollToTop />
                          <Routes>
                            <Route path="/" element={<Home />} />
                            <Route path="/recherches" element={<SearchResults />} />
                            <Route path="/agenda" element={<Agenda />} />
                            <Route path="/actualite" element={<Blog />} />
                            <Route path="/carte" element={<MapPage />} />
                            <Route path="/contact" element={<ContactPage />} />
                            <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
                            <Route path="/cookies" element={<CookiesPage />} />
                            <Route path="/ecrire-au-maire" element={<EcrireMairePage />} />
                            <Route path="/les-actes-administratifs" element={<ActesAdministratifsPage />} />
                            <Route path="/associations" element={<AssociationsPage />} />
                            <Route path="/numero-d-urgence" element={<UrgenceNumbersPage />} />
                            <Route path="/webcams" element={<WebcamPage />} />
                            <Route path="/point-interet/:slug" element={<PointOfInterestPage />} />
                            <Route path="/actualite/:newsSlug" element={<NewsPage />} />
                            <Route path="/categorie/ville-de-noel" element={<HardRedirect to="/ville-de-noel" />} />
                            <Route path="/categorie/:categorySlug" element={<CategoriePage />} />
                            <Route path="/evenement/:eventSlug" element={<EventPage />} />
                            <Route path="/page/:categorySlug/:pageSlug" element={<Page />} />
                            <Route path="/page/:pageSlug" element={<Page />} />
                            <Route path="/kiosque" element={<KioskPage />} />
                            <Route path="/kiosque/:serieSlug" element={<KioskSeriePage />} />
                            <Route path="/kiosque/:serieSlug/:magazineSlug" element={<MagazinePage />} />
                            <Route path="/associations/:associationSlug" element={<AssociationPage />} />
                            <Route path="/:subSiteSlug" element={<DynamicSubSiteRoute />} />
                            <Route path="/:subSiteSlug/page/:pageSlug" element={<Page />} />
                            <Route path="/:subSiteSlug/recherches" element={<SearchResultsSubsite />} />
                            <Route path="/:subSiteSlug/agenda" element={<Agenda />} />
                            <Route path="/:subSiteSlug/evenement/:eventSlug" element={<EventPage />} />
                            <Route path="/:subSiteSlug/actualite" element={<Blog />} />
                            <Route path="/:subSiteSlug/actualite/:newsSlug" element={<NewsPage />} />
                            <Route path="/:subSiteSlug/carte" element={<MapPage />} />
                            <Route path="/:subSiteSlug/commercants" element={<MerchantsPage />} />
                            <Route path="/:subSiteSlug/commercants/:merchantSlug" element={<MerchantPage />} />
                            {isRedirectionEnabled && redirectionRoutes.map(route => route)}
                            <Route path="*" element={<NotFoundPage />} />
                          </Routes>
                          <Footer />
                        </Layout>
                        <ProfileSidebar />
                        <AccessibilitySidebar />
                        <Sidebar />
                      </SubSiteProvider>
                    </ClosedSubSiteGate>
                  )}
                  <Modal />
                </RecaptchaWrapper>
              </ModalProvider>
            </ProfileProvider>
          </ProfileSidebarProvider>
        </AccessibilitySidebarProvider>
      </AccessibilityProvider>
    </div>
  );
}

export default AppContent;

